import type { ReactElement, ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { BottomBarOverrideContext } from "./BottomBarOverrideContext";
import { ChromeVisibilityContext } from "./ChromeVisibilityContext";
import { CreateActionContext } from "./CreateActionContext";
import { DrawerOverrideContext } from "./DrawerOverrideContext";
import { MainClassNameContext } from "./MainClassNameContext";
import { SettingsHeaderOverrideContext } from "./SettingsHeaderOverrideContext";
import { TabsOverrideContext } from "./TabsOverrideContext";
import { TitleOverrideContext } from "./TitleOverrideContext";
import { TopBarOverrideContext } from "./TopBarOverrideContext";
import { TrailingOverrideContext } from "./TrailingOverrideContext";
import type {
  MobileSectionTabsOverride,
  MobileSettingsHeaderOverride,
  MobileTrailingOverride,
} from "./types";
import { useRefCountedFlag } from "./useRefCountedFlag";

/**
 * Mounts one independent React context per mobile-shell chrome concern
 * (tabs, title, topBar, bottomBar, trailing, settingsHeader,
 * chrome-visibility, mainClassName, createAction, drawerOverride).
 *
 * Each context below has its own independently-memoized value, so a write
 * to one only re-renders that context's own subscribers.
 */
export const MobileSectionTabsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // --- tabsOverride ---
  const [tabsOverride, setTabsOverride] =
    useState<MobileSectionTabsOverride | null>(null);
  const updateSelectedValue = useCallback((value: string) => {
    setTabsOverride(prev => (prev ? { ...prev, selectedValue: value } : prev));
  }, []);
  const tabsOverrideValue = useMemo(
    () => ({ tabsOverride, setTabsOverride, updateSelectedValue }),
    [tabsOverride, updateSelectedValue],
  );

  // --- titleOverride ---
  const [titleOverride, setTitleOverride] = useState<string | null>(null);
  const titleOverrideValue = useMemo(
    () => ({ titleOverride, setTitleOverride }),
    [titleOverride],
  );

  // --- topBarOverride ---
  const [topBarOverride, setTopBarOverride] = useState<ReactElement | null>(
    null,
  );
  const topBarOverrideValue = useMemo(
    () => ({ topBarOverride, setTopBarOverride }),
    [topBarOverride],
  );

  // --- bottomBarOverride ---
  const [bottomBarOverride, setBottomBarOverride] =
    useState<ReactElement | null>(null);
  const bottomBarOverrideValue = useMemo(
    () => ({ bottomBarOverride, setBottomBarOverride }),
    [bottomBarOverride],
  );

  // --- trailingOverride ---
  const [trailingOverride, setTrailingOverride] =
    useState<MobileTrailingOverride | null>(null);
  const trailingOverrideValue = useMemo(
    () => ({ trailingOverride, setTrailingOverride }),
    [trailingOverride],
  );

  // --- settingsHeaderOverride ---
  const [settingsHeaderOverride, setSettingsHeaderOverride] =
    useState<MobileSettingsHeaderOverride | null>(null);
  const settingsHeaderOverrideValue = useMemo(
    () => ({ settingsHeaderOverride, setSettingsHeaderOverride }),
    [settingsHeaderOverride],
  );

  // --- drawerOverride ---
  const [drawerOverride, setDrawerOverride] = useState<ReactElement | null>(
    null,
  );
  const drawerOverrideValue = useMemo(
    () => ({ drawerOverride, setDrawerOverride }),
    [drawerOverride],
  );

  // --- chrome visibility ---
  const [isAvatarHidden, setIsAvatarHidden] = useRefCountedFlag();
  const [isChromeHidden, setIsChromeHidden] = useRefCountedFlag();
  const [isBottomNavHidden, setIsBottomNavHidden] = useRefCountedFlag();
  const [isTopNavHidden, setIsTopNavHidden] = useRefCountedFlag();
  const [isFullBleedContent, setIsFullBleedContent] = useRefCountedFlag();
  const [isFixedHeightContent, setIsFixedHeightContent] = useRefCountedFlag();
  const [isHideChromeOnScrollDisabled, setIsHideChromeOnScrollDisabled] =
    useRefCountedFlag();
  const [isTopBarUnbranded, setIsTopBarUnbranded] = useRefCountedFlag();
  const chromeVisibilityValue = useMemo(
    () => ({
      isAvatarHidden,
      setIsAvatarHidden,
      isChromeHidden,
      setIsChromeHidden,
      isBottomNavHidden,
      setIsBottomNavHidden,
      isTopNavHidden,
      setIsTopNavHidden,
      isFullBleedContent,
      setIsFullBleedContent,
      isFixedHeightContent,
      setIsFixedHeightContent,
      isHideChromeOnScrollDisabled,
      setIsHideChromeOnScrollDisabled,
      isTopBarUnbranded,
      setIsTopBarUnbranded,
    }),
    [
      isAvatarHidden,
      setIsAvatarHidden,
      isChromeHidden,
      setIsChromeHidden,
      isBottomNavHidden,
      setIsBottomNavHidden,
      isTopNavHidden,
      setIsTopNavHidden,
      isFullBleedContent,
      setIsFullBleedContent,
      isFixedHeightContent,
      setIsFixedHeightContent,
      isHideChromeOnScrollDisabled,
      setIsHideChromeOnScrollDisabled,
      isTopBarUnbranded,
      setIsTopBarUnbranded,
    ],
  );

  // --- mainClassName ---
  // Tagged with the owning registrant's token (see `setMainClassName`) so
  // `clearMainClassName` can tell whether it's still the current owner
  // before clearing.
  const [mainClassNameEntry, setMainClassNameEntry] = useState<{
    className: string;
    token: object;
  } | null>(null);
  const mainClassName = mainClassNameEntry?.className ?? null;
  const setMainClassName = useCallback((token: object, className: string) => {
    setMainClassNameEntry({ token, className });
  }, []);
  const clearMainClassName = useCallback((token: object) => {
    setMainClassNameEntry(current =>
      current?.token === token ? null : current,
    );
  }, []);
  const mainClassNameValue = useMemo(
    () => ({ mainClassName, setMainClassName, clearMainClassName }),
    [mainClassName, setMainClassName, clearMainClassName],
  );

  // --- createAction ---
  const [onCreateOpen, setOnCreateOpenState] = useState<(() => void) | null>(
    null,
  );
  const setOnCreateOpen = useCallback((handler: (() => void) | null) => {
    setOnCreateOpenState(() => handler);
  }, []);
  const createActionValue = useMemo(
    () => ({ onCreateOpen, setOnCreateOpen }),
    [onCreateOpen, setOnCreateOpen],
  );

  return (
    <TabsOverrideContext.Provider value={tabsOverrideValue}>
      <TitleOverrideContext.Provider value={titleOverrideValue}>
        <TopBarOverrideContext.Provider value={topBarOverrideValue}>
          <BottomBarOverrideContext.Provider value={bottomBarOverrideValue}>
            <TrailingOverrideContext.Provider value={trailingOverrideValue}>
              <SettingsHeaderOverrideContext.Provider
                value={settingsHeaderOverrideValue}
              >
                <ChromeVisibilityContext.Provider value={chromeVisibilityValue}>
                  <MainClassNameContext.Provider value={mainClassNameValue}>
                    <CreateActionContext.Provider value={createActionValue}>
                      <DrawerOverrideContext.Provider
                        value={drawerOverrideValue}
                      >
                        {children}
                      </DrawerOverrideContext.Provider>
                    </CreateActionContext.Provider>
                  </MainClassNameContext.Provider>
                </ChromeVisibilityContext.Provider>
              </SettingsHeaderOverrideContext.Provider>
            </TrailingOverrideContext.Provider>
          </BottomBarOverrideContext.Provider>
        </TopBarOverrideContext.Provider>
      </TitleOverrideContext.Provider>
    </TabsOverrideContext.Provider>
  );
};
